import { ON_REQUEST_INTEGRATIONS, type OnRequestIntegration } from "@kontaktfix/shared";
import { Badge, Button, Card, CardHeader } from "@kontaktfix/ui";
import { CheckIcon, FileCsvIcon, PaperPlaneTiltIcon, PlusIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { useMe } from "@/lib/queries";

type Requested = { requested: OnRequestIntegration[] };
const KEY = ["integrations"] as const;

/** Settings → Integrations (issue #34): what is built, and connectors on request. */
export function IntegrationsTab() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: me } = useMe();
  const { data } = useQuery({ queryKey: KEY, queryFn: () => api.get<Requested>("/api/integrations") });
  const [busy, setBusy] = useState<OnRequestIntegration | null>(null);
  const isAdmin = me?.role === "admin";

  const request = async (integration: OnRequestIntegration) => {
    setBusy(integration);
    try {
      client.setQueryData(KEY, await api.post<Requested>("/api/integrations/request", { integration }));
      toast.success(t("integrations.requestSent"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader title={t("integrations.available")} description={t("integrations.availableText")} />
        <ul className="divide-y divide-border">
          {(
            [
              { key: "directory", icon: <UsersThreeIcon className="size-5" /> },
              { key: "file", icon: <FileCsvIcon className="size-5" /> },
            ] as const
          ).map(({ key, icon }) => (
            <li key={key} className="flex flex-wrap items-center gap-4 px-5 py-4">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-subtle text-fg-muted">
                {icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium">{t(`integrations.built.${key}.name`)}</div>
                <p className="text-[12.5px] text-fg-muted">{t(`integrations.built.${key}.text`)}</p>
              </div>
              {isAdmin && (
                <Button size="sm" asChild>
                  <Link to="/lists" search={{ new: key }}>
                    <PlusIcon />
                    {t("integrations.createList")}
                  </Link>
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <CardHeader title={t("integrations.onRequest")} description={t("integrations.onRequestText")} />
        <ul className="grid border-border border-t sm:grid-cols-2">
          {ON_REQUEST_INTEGRATIONS.map((key) => {
            const requested = data?.requested.includes(key) ?? false;
            return (
              <li
                key={key}
                className="flex flex-col gap-2 border-border border-b px-5 py-4 last:border-b-0 sm:odd:border-r sm:[&:nth-last-child(2):nth-child(odd)]:border-b-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13.5px] font-medium">
                    {t(`integrations.connectors.${key}.name`)}
                  </span>
                  {requested ? (
                    <Badge tone="success">
                      <CheckIcon />
                      {t("integrations.requested")}
                    </Badge>
                  ) : (
                    <Badge tone="neutral">{t("integrations.onRequestBadge")}</Badge>
                  )}
                </div>
                <p className="flex-1 text-[12.5px] text-fg-muted">
                  {t(`integrations.connectors.${key}.text`)}
                </p>
                {isAdmin && !requested && (
                  <div>
                    <Button size="sm" onClick={() => request(key)} loading={busy === key}>
                      <PaperPlaneTiltIcon />
                      {t("integrations.request")}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
